// src/services/gruposService.js
import { getAuthHeader } from "./authService";

/** Institución fija (dato quemado) */
export const INSTITUCION_ID = "16020324-f0ea-4319-905c-5a30618ce9f5";

/** Siempre en :3001 según tu instrucción */
const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/v1/grupos`;

/* ========== Helpers ========== */
const toJson = (res) => res.json().catch(() => ({}));
const ensureOk = (res, data) => {
  if (!res.ok) {
    const msg =
      data?.message ||
      data?.error ||
      (typeof data === "string" ? data : "") ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
};
const clean = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
};

/**
 * GET /api/v1/grupos/:contextInstitutionId
 * Obtiene todos los grupos deportivos, opcionalmente filtrados por institución
 * @param {string} institucionId - ID de la institución para filtrar grupos (opcional)
 */
export async function listGrupos(institucionId = INSTITUCION_ID) {
  const res = await fetch(`${BASE_URL}/${institucionId}`, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  // Esperamos un array; si viene envuelto, intenta data.data
  return Array.isArray(data) ? data : data?.data ?? [];
}

/**
 * GET /api/v1/deportistas/grupo/:grupoId
 * Obtiene todos los deportistas activos de un grupo específico
 * @param {string} grupoId - ID del grupo
 */
export async function getDeportistasByGrupo(grupoId) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/deportistas/grupo/${grupoId}`, {
    headers: { Accept: "application/json", ...getAuthHeader() },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return Array.isArray(data) ? data : data?.data ?? [];
}

/**
 * POST /api/v1/grupos/:contextInstitutionId
 * Crea un nuevo grupo deportivo en una institución
 * @param {Object} form - Datos del nuevo grupo
 * @param {string} institucionId - ID de la institución (opcional, por defecto usa INSTITUCION_ID)
 */
export async function createGrupo(form, institucionId = INSTITUCION_ID) {
  const payload = clean({
    institucion_id: institucionId,
    entrenador_id: form.entrenador_principal_id || form.entrenador_id,
    entrenador_secun_id: form.entrenador_secun_id || form.entrenador_auxiliar_id,
    nombre: form.nombre,
    categoria: form.categoria,
    edad_minima: toIntOr(form.edad_minima, undefined),
    edad_maxima: toIntOr(form.edad_maxima || form.edad_max, undefined),
    descripcion: form.descripcion,
    horarios: clean({
      lunes: form.horarios?.lunes,
      martes: form.horarios?.martes,
      miercoles: form.horarios?.miercoles,
      jueves: form.horarios?.jueves,
      viernes: form.horarios?.viernes,
      sabado: form.horarios?.sabado,
      domingo: form.horarios?.domingo,
    }),
  });

  const res = await fetch(`${BASE_URL}/${institucionId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? data;
}

/**
 * PUT /api/v1/grupos/:id
 * Actualiza la información de un grupo deportivo existente
 * @param {string} id - ID del grupo a actualizar
 * @param {Object} form - Datos a actualizar
 */
export async function updateGrupo(id, form) {
  const payload = clean({
    institucion_id: form.institucion_id || INSTITUCION_ID,
    entrenador_id: form.entrenador_principal_id || form.entrenador_id,
    entrenador_secun_id: form.entrenador_secun_id || form.entrenador_auxiliar_id,
    nombre: form.nombre,
    categoria: form.categoria,
    edad_minima: toIntOr(form.edad_minima, undefined),
    edad_maxima: toIntOr(form.edad_maxima || form.edad_max, undefined),
    descripcion: form.descripcion,
    horarios: clean({
      lunes: form.horarios?.lunes,
      martes: form.horarios?.martes,
      miercoles: form.horarios?.miercoles,
      jueves: form.horarios?.jueves,
      viernes: form.horarios?.viernes,
      sabado: form.horarios?.sabado,
      domingo: form.horarios?.domingo,
    }),
  });

  const res = await fetch(`${BASE_URL}/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? data;
}

/**
 * DELETE /api/v1/grupos/:id
 * Elimina (desactiva) un grupo deportivo del sistema mediante soft delete
 * @param {string} id - ID del grupo a eliminar
 */
export async function deleteGrupo(id) {
  const res = await fetch(`${BASE_URL}/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeader(),
    },
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return true;
}

function toIntOr(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * PUT /api/v1/grupos/assign-deportistas/:idGrupo
 * Asigna en lote una lista de deportistas a un grupo específico
 * @param {string} idGrupo - ID del grupo al que se asignarán los deportistas
 * @param {Array} deportistas - Lista de objetos con deportista_id
 */
export async function assignDeportistasToGrupo(idGrupo, deportistas) {
  const payload = {
    deportistas: Array.isArray(deportistas) 
      ? deportistas.map(d => ({ deportista_id: d.deportista_id || d.id }))
      : []
  };

  const res = await fetch(`${BASE_URL}/assign-deportistas/${idGrupo}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });
  const data = await toJson(res);
  ensureOk(res, data);
  return data?.data ?? data;
}